import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { settingsSelectors } from 'store/settings'
import clsx from 'clsx'
import { Icon } from 'components/ui/Icon'
import { UIButton } from 'components/ui/Button'
import { ComposerTextarea } from 'components/ui/ComposerTextarea'
import { QuestionChip } from 'components/ui/QuestionChip'
import { SelectablePill } from 'components/ui/SelectablePill'
import { AreaCombobox } from 'components/ui/AreaCombobox'
import { getSelectableAreaOptions } from 'utils/areas'
import { useGetSetting } from 'api/settings'
import { useGetAreas } from 'api/areas'
import { useGetDefaultQuestions } from 'api/default-questions'
import { getDefaultQuestionTexts } from 'hooks/useSuggestedQuestions'
import { openNewChat } from 'api/chats'

type ChatHeroProps = {
  areaId?: string
  onPendingQuestion: (question: string) => void
  onPendingCancelled: () => void
}

export const ChatHero = ({ areaId, onPendingQuestion, onPendingCancelled }: ChatHeroProps) => {
  const { t, i18n } = useTranslation()
  const { setting, title, description } = useGetSetting()
  const { data: areas } = useGetAreas()
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>(undefined)
  const [userInput, setUserInput] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // In the multi-area flow the server picks the areas: no picker, no areaId
  const isMultiArea = useSelector(settingsSelectors.isMultiAreaFlow)

  const areaOptions = useMemo(() => getSelectableAreaOptions(areas), [areas])
  // Any nesting -> searchable combobox; a purely flat area set keeps the simpler pills.
  const hasSubAreas = useMemo(() => (areas ?? []).some((area) => (area.children?.length ?? 0) > 0), [areas])

  const resolvedAreaId = useMemo(() => {
    if (areaId) return areaId
    if (areaOptions.length === 1) return areaOptions[0].area._id
    return selectedAreaId
  }, [areaId, areaOptions, selectedAreaId])

  const areaIds = !isMultiArea && resolvedAreaId ? [resolvedAreaId] : undefined
  const { defaultQuestions } = useGetDefaultQuestions(areaIds)

  const questions = useMemo(
    () => getDefaultQuestionTexts(defaultQuestions, i18n.language),
    [defaultQuestions, i18n.language],
  )

  const showAreaPicker = !isMultiArea && !areaId && areaOptions.length > 1

  // Preselect the first selectable area so the user can start typing right away; they can change it
  // from the dropdown.
  useEffect(() => {
    if (showAreaPicker && !selectedAreaId && areaOptions.length) {
      setSelectedAreaId(areaOptions[0].area._id)
    }
  }, [showAreaPicker, selectedAreaId, areaOptions])

  const canType = isMultiArea || !!resolvedAreaId
  const canSend = canType && userInput.trim().length > 0 && !submitting

  const submit = async (text: string) => {
    const question = text.trim()
    if (!question || submitting || (!isMultiArea && !resolvedAreaId)) return
    setSubmitting(true)
    onPendingQuestion(question)
    try {
      await openNewChat(isMultiArea ? {} : { areaId: resolvedAreaId })
    } catch (e) {
      console.error({ e })
      onPendingCancelled()
      setSubmitting(false)
    }
  }

  return (
    <div className="scrollbar-hidden tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-overflow-y-auto tw-px-4 tw-py-8">
      <div className="animate-fade-slide-in tw-flex tw-w-full tw-max-w-2xl tw-flex-col tw-items-center tw-text-center">
        {setting?.logo?.full && (
          <img
            src={setting?.logo?.thumbnail?.medium ?? setting?.logo?.full}
            alt="Logo"
            className="tw-mb-5 tw-h-32 tw-w-32 tw-rounded-2xl tw-object-cover"
          />
        )}
        <h1 className="tw-m-0 tw-text-3xl tw-font-bold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h1>
        <p className="tw-mb-12 tw-mt-2 tw-text-base" style={{ color: 'var(--text-secondary)' }}>
          {description}
        </p>

        {showAreaPicker && (
          <div className="tw-mb-4 tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2">
            <span className="tw-text-sm" style={{ color: 'var(--text-secondary)' }}>
              {t('selectAreaHint')}
            </span>
            {hasSubAreas ? (
              <AreaCombobox
                options={areaOptions}
                selectedAreaId={selectedAreaId}
                onSelect={setSelectedAreaId}
                triggerPlaceholder={t('chooseArea')}
                searchPlaceholder={t('searchArea')}
                noResultsLabel={t('noAreasFound')}
              />
            ) : (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2">
                {areas?.map((area) => (
                  <SelectablePill
                    key={area._id}
                    label={area.name}
                    selected={area._id === selectedAreaId}
                    disabled={area.numberOfAreaSources === 0}
                    onClick={() => setSelectedAreaId(area._id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div
          className="tw-flex tw-w-full tw-items-end tw-rounded-2xl tw-px-2 tw-py-2"
          style={{ background: 'var(--input-bg)', border: '1px solid var(--border-default)' }}
        >
          <ComposerTextarea
            value={userInput}
            onChange={setUserInput}
            onSubmit={() => submit(userInput).catch(console.error)}
            placeholder={canType ? t('typeMessage') : t('chooseArea')}
            disabled={!canType || submitting}
            maxHeight={160}
            autoFocusWhenEnabled
            className={clsx('tw-text-[var(--text-primary)]', !canType && 'tw-cursor-not-allowed')}
          />
          <UIButton
            type="button"
            disabled={!canSend}
            loading={submitting}
            onClick={() => {
              submit(userInput).catch(console.error)
            }}
            className="tw-w-10 tw-shrink-0"
          >
            <Icon icon={['far', 'paper-plane-top']} className="tw-h-5 tw-w-5" style={{ color: 'white' }} />
          </UIButton>
        </div>

        {questions.length > 0 && (
          <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2">
            {questions.map((question, index) => (
              <QuestionChip
                key={question}
                question={question}
                disabled={!canType || submitting}
                onClick={() => submit(question).catch(console.error)}
                animationDelayMs={100 + index * 80}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
